import { Add } from "@mui/icons-material";
import { Checkbox, CircularProgress, OutlinedInput, Stack } from "@mui/material";
import clsx from "clsx";
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { LoadingButton } from "../../../../common/components/LoadingButton";
import { LoadingButton as MuiLoadingButton } from "@mui/lab";
import { NetworkRequestGate } from "../../../../common/components/NetworkRequestGate";
import { useAllCollectedPIC } from "../../../../common/hooks/dataMapHooks";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { NetworkRequestState } from "../../../../common/models";
import { Api } from "../../../../common/service/Api";
import {
  AllCollectionGroupTypes,
  CollectionGroupType,
} from "../../../../common/service/server/controller/CollectionGroupsController";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import { CategoryDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import {
  DefProcessingActivityDto,
  LawfulBasis,
  ProcessingActivityDto,
} from "../../../../common/service/server/dto/ProcessingActivityDto";
import { Callout, CalloutVariant } from "../../../components/Callout";
import { EmptyInfo } from "../../../components/Empty";
import { QuestionLabel } from "../../../components/get-compliant/survey/QuestionContainer";
import { SurveyBooleanQuestion } from "../../../components/get-compliant/survey/SurveyBooleanQuestion";
import { CheckboxControlLabel } from "../../../components/input/CheckboxControlLabel";
import {
  useOrganizationVendors,
  usePrimaryOrganization,
  usePrimaryOrganizationId,
} from "../../../hooks";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import {
  AssociatePicMutation,
  useDefaultActivityCheckboxes,
  useProcessingActivites,
} from "../../../hooks/useProcessingActivities";
import { SurveyGateButton } from "../../../components/organisms/survey/SurveyGateButton";
import { Disableable } from "../../../components";
import { ConfirmDialog } from "../../../components/dialog";
import { ProcessingRegion } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { TodoListCallout } from "../../../components/organisms/TodoListCallout";
import * as MuiIcon from "@mui/icons-material";
import { Pill, SimpleCheckboxControlled, Toggle, ToolTip, useSnackBar } from "@truevault/aquilo";
import { Link } from "react-router-dom";
import { Routes } from "../../../root/routes";
import { SparklesIcon } from "@heroicons/react/20/solid";
import { Cog6ToothIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useActionRequest } from "../../../../common/hooks/api";
import {
  LawfulBasisExtendedOptionsVisibility,
  ProcessingActivityLawfulBasisSelect,
} from "../../../components/organisms/ProcessingActivityLawfulBasisSelect";
import {
  EEA_UK_SURVEY_NAME,
  EXTRA_LAWFUL_BASES_PUBLIC_INTEREST_SLUG,
  EXTRA_LAWFUL_BASES_VITAL_INTEREST_SLUG,
} from "../../../surveys/eeaUkSurvey";

type CollectionPurposesProps = {
  consumerGroups: CollectionGroupDetailsDto[];
  picWarning: "top" | "bottom";
  excludeCollectionGroupType?: CollectionGroupType[];
  hideTitles?: boolean;
  setCurrentRequest?: (req: NetworkRequestState) => void;
  setComplete?: (complete: boolean) => void;
  readonly?: boolean;
};

export const COLLECTION_PURPOSE_SURVEY_SLUG = "collection-purpose-survey";

export const CollectionPurposes: React.FC<CollectionPurposesProps> = ({
  consumerGroups,
  picWarning,
  setComplete,
  readonly,
}) => {
  const [org] = usePrimaryOrganization();
  const orgId = org?.id;
  const [, orgVendorsRequest] = useOrganizationVendors(orgId);
  const { data: dataMap, request: dataMapRequest } = useDataMap(orgId);
  const groupTypes = consumerGroups.map((cg) => cg.collectionGroupType);
  const excluded = AllCollectionGroupTypes.filter((type) => !groupTypes.includes(type));
  const collectedPic = useAllCollectedPIC(dataMap, excluded);
  const { answers, setAnswer } = useSurvey(orgId, COLLECTION_PURPOSE_SURVEY_SLUG);

  const [activities, activityRequest, refreshActivities, assocationMutation] =
    useProcessingActivites(orgId);

  // CUSTOM ACTIVITIES
  const custom = useMemo(
    () => activities?.filter((a) => !a.defaultId && a.regions?.includes("UNITED_STATES")) ?? [],
    [activities],
  );
  const [customDeleteId, setCustomDeleteId] = useState(null);
  const [addCustomLoading, setAddCustomLoading] = useState(false);
  const handleAddCustomActivity = async (name: string) => {
    if (activities.map((a) => a.name).includes(name)) return;

    setAddCustomLoading(true);
    try {
      await Api.processingActivities.createProcessingActivity(orgId, {
        name: name,
        defaultId: undefined,
        lawfulBases: undefined,
        regions: ["UNITED_STATES", "EEA_UK", "CANADA"],
      });
      await refreshActivities();
    } finally {
      setAddCustomLoading(false);
    }
  };

  const finishRemoveCustomActivity = async (id: string) => {
    let regions: ProcessingRegion[] = ["UNITED_STATES"];
    if (activities?.find((a) => a.id == id && a.regions?.includes("EEA_UK"))) {
      regions.push("EEA_UK");
    }
    if (activities?.find((a) => a.id == id && a.regions?.includes("CANADA"))) {
      regions.push("CANADA");
    }
    await Api.processingActivities.deleteProcessingActivityRegion(orgId, id, regions);
    await refreshActivities();
  };

  const handleRemoveCustomActivity = (id: string) => {
    setCustomDeleteId(id);
  };

  const closeRemoveCustomActivity = () => setCustomDeleteId(null);
  // END CUSTOM ACTIVITIES

  const notProcessedPic = useMemo(() => {
    const allProcessedPic = _.uniq((activities ?? []).flatMap((a) => a.processedPic));
    return collectedPic?.filter((c) => !allProcessedPic.includes(c.id)) ?? [];
  }, [activities, collectedPic]);

  const defCheckboxes = useDefaultActivityCheckboxes(
    orgId,
    activities,
    "UNITED_STATES",
    ["UNITED_STATES", "EEA_UK", "CANADA"],
    refreshActivities,
  );

  const anySelected = useMemo(
    () => activities?.some((a) => a.processedPic.length > 0),
    [activities],
  );

  const allCheckedHaveSelections = useMemo(() => {
    return defCheckboxes.props.checked.every((id) => {
      const act = activities?.find((a) => a.defaultId == id);
      if (act) {
        return act.processedPic.length > 0;
      }
    });
  }, [defCheckboxes.props.checked, activities]);

  const finishedCustom =
    answers["has-custom-processing-activities"] == "false" || custom.length > 0;

  useEffect(() => {
    setComplete?.(
      anySelected && allCheckedHaveSelections && notProcessedPic.length === 0 && finishedCustom,
    );
  }, [anySelected, allCheckedHaveSelections, notProcessedPic, finishedCustom, setComplete]);

  return (
    <NetworkRequestGate
      requests={[
        dataMapRequest,
        orgVendorsRequest,
        activityRequest,
        defCheckboxes.requests.defaultActivities,
      ]}
    >
      <h4 className="intermediate">{org.name} uses personal data for:</h4>
      <Stack className="ml-xl" spacing={2}>
        {notProcessedPic.length > 0 && picWarning == "top" && (
          <TodoListCallout
            text={
              "The following personal information categories are not selected for any collection purposes:"
            }
            groups={notProcessedPic}
          />
        )}
        <div className="mt-lg" />
        {defCheckboxes.props.defaults?.map((defActivity) => {
          return (
            <CollectionPurposeCollapseCard
              key={defActivity.id}
              defaultActivity={defActivity}
              activities={activities}
              checked={defCheckboxes.props.checked}
              setChecked={defCheckboxes.props.setChecked}
              categories={collectedPic}
              assocationMutation={assocationMutation}
              readonly={readonly}
              variant="checkbox"
            />
          );
        })}
      </Stack>
      <Stack spacing={2}>
        <div className="mt-lg" />
        <SurveyGateButton
          className="ml-xl w-192"
          label="Done"
          slug="lawful-bases-default-done"
          survey={answers}
          updateResponse={setAnswer}
        >
          <QuestionLabel label="Additional Processing Activities" />
          <SurveyBooleanQuestion
            answer={answers["has-custom-processing-activities"]}
            onAnswer={(ans) => setAnswer("has-custom-processing-activities", ans)}
            question={{
              type: "boolean",
              slug: "has-custom-processing-activities",
              question:
                "Does your business engage in any core data processing activities that are not listed above?",
              helpText:
                " Think about data processing specific to your type of business, like “Connecting people with local contractors,” “Providing eye prescriptions,” or “Determining eligibility for scholarships.” If you’re an eCommerce company, the list above likely covers your core data processing activities.",
              yesLabel: "Yes, add more",
              noLabel: "Nope, that covers it",
              visible: true,
            }}
          />
          {answers["has-custom-processing-activities"] === "true" && (
            <CustomPA
              custom={custom}
              categories={collectedPic}
              associationMutation={assocationMutation}
              variant="text"
              open
              onAdd={handleAddCustomActivity}
              addLoading={addCustomLoading}
              onRemove={handleRemoveCustomActivity}
            />
          )}

          {notProcessedPic.length > 0 && picWarning == "bottom" && (
            <TodoListCallout
              text={
                "The following personal information categories are not selected for any collection purposes:"
              }
              groups={notProcessedPic}
            />
          )}
        </SurveyGateButton>
      </Stack>
      <ConfirmDialog
        open={Boolean(customDeleteId)}
        onClose={closeRemoveCustomActivity}
        onConfirm={() => {
          finishRemoveCustomActivity(customDeleteId);
          closeRemoveCustomActivity();
        }}
        confirmTitle="Are you sure?"
        contentText="Are you sure you would like to remove this Purpose?"
        confirmText="Yes"
        cancelText="No"
      />
    </NetworkRequestGate>
  );
};

type CustomPAProps = {
  custom: ProcessingActivityDto[];
  categories: CategoryDto[];
  associationMutation: AssociatePicMutation;
  variant: CollectionPurposeCollapseCardVariant;
  open?: boolean;
  addLoading: boolean;
  indentAddRow?: boolean;
  onAdd: (name: string) => Promise<void>;
  onRemove: (id: string) => void;
};

export const CustomPA: React.FC<CustomPAProps> = ({
  custom,
  categories,
  associationMutation,
  variant,
  open,
  addLoading,
  indentAddRow = false,
  onAdd,
  onRemove,
}) => {
  const [customName, setCustomName] = useState("");

  const handleAddClick = async () => {
    if (customName == "") return;
    await onAdd(customName);
    setCustomName("");
  };

  return (
    <>
      <div className={clsx("flex flex-row my-mdlg", { "ml-lg": indentAddRow })}>
        <OutlinedInput
          className="w-512"
          value={customName}
          onChange={(e) => {
            setCustomName(e.target.value);
          }}
          placeholder="Add a processing activity, keep it short and sweet!"
          disabled={addLoading}
        />
        <LoadingButton
          loading={addLoading}
          onClick={handleAddClick}
          variant="contained"
          color="primary"
          startIcon={<Add />}
          className="ml-md px-md"
        >
          Add
        </LoadingButton>
      </div>

      <Stack spacing={4} className="ml-lg">
        {custom.map((a) => {
          return (
            <div key={a.id}>
              <CollectionPurposeCollapseCard
                activity={a}
                activities={custom}
                variant={variant}
                overrideText={variant == "text" ? "Remove" : undefined}
                open={open}
                checked={[]}
                setChecked={(id, _) => onRemove(id)}
                categories={categories}
                assocationMutation={associationMutation}
                readonly={false}
                collapsible={true}
              />
            </div>
          );
        })}
      </Stack>
    </>
  );
};

export type CollectionPurposeCollapseCardVariant = "checkbox" | "text" | "none";
type CollectionPurposeCollapseCardProps = {
  defaultActivity?: DefProcessingActivityDto | undefined;
  activity?: ProcessingActivityDto | undefined;
  activities: ProcessingActivityDto[];
  refreshActivities?: () => Promise<void>;
  variant: CollectionPurposeCollapseCardVariant;
  overrideText?: string;
  checked: string[];
  setChecked: (defaultId: string, checked: boolean) => void;
  categories: CategoryDto[];
  assocationMutation: AssociatePicMutation;
  readonly?: boolean;
  open?: boolean;
  collapsible?: boolean;
  useAquiloDesign?: boolean;
  showGdprLawfulBases?: boolean;
  showCustomActions?: boolean;
  onEdit?: () => void;
  onRemove?: () => void;
  /** DES-30 recommendation provenance: renders a "Why we recommend" link beside the name.
   *  Vendor evidence ("Because you are using: …") leads; the popularity line is the fallback. */
  recommendation?: {
    vendors?: { name: string; logoUrl?: string }[];
  };
};

export const CollectionPurposeCollapseCard: React.FC<CollectionPurposeCollapseCardProps> = ({
  defaultActivity,
  activity,
  activities,
  refreshActivities,
  variant,
  overrideText,
  checked,
  setChecked,
  categories,
  assocationMutation,
  readonly,
  open,
  collapsible = true,
  useAquiloDesign = false,
  showGdprLawfulBases = false,
  showCustomActions = false,
  onEdit,
  onRemove,
  recommendation,
}) => {
  const orgId = usePrimaryOrganizationId();
  const showSnackbar = useSnackBar();
  const isEnabled = checked.includes(defaultActivity?.id) || checked.includes(activity?.id);

  const realActivity: ProcessingActivityDto | undefined = useMemo(
    () => activity ?? activities.find((a) => a.defaultId == defaultActivity.id),
    [activity, defaultActivity, activities],
  );

  const processed = useMemo(() => {
    const processedPic = realActivity ? [...realActivity.processedPic] : [];
    const pendingUpdates = realActivity?._local?.processedPicPendingUpdates ?? [];

    pendingUpdates.forEach((up) => {
      const existingIdx = processedPic.indexOf(up.id);
      if (up.type == "add" && existingIdx == -1) {
        processedPic.push(up.id);
      } else if (up.type == "remove" && existingIdx >= 0) {
        processedPic.splice(existingIdx, 1);
      }
    });

    return new Set(processedPic);
  }, [realActivity]);

  const localProcessedUpdate = useMemo(
    () => new Set(realActivity?._local?.processedPicPendingUpdates?.map((up) => up.id) || []),
    [realActivity?._local?.processedPicPendingUpdates],
  );
  const isUpdatingCategories = localProcessedUpdate.size > 0;

  const enabledLabel = useMemo(() => {
    return [...processed]
      .map((id) => categories.find((c) => c.id == id)?.name)
      .filter((c) => c)
      .join(", ");
  }, [processed, categories]);

  const { fetch: addGdprProcessingActivity } = useActionRequest({
    api: (activityId: string) =>
      Api.processingActivities.patchUpdateProcessingActivity(orgId, activityId, {
        regions: Array.from(new Set([...realActivity.regions, "EEA_UK"])),
        name: undefined,
        lawfulBases: undefined,
      }),
    onSuccess: () => {
      if (refreshActivities) {
        refreshActivities();
      }
    },
  });

  const { fetch: removeGdprProcessingActivity } = useActionRequest({
    api: (activityId: string) =>
      Api.processingActivities.deleteProcessingActivityRegion(orgId, activityId, ["EEA_UK"]),
    onSuccess: () => {
      if (refreshActivities) {
        refreshActivities();
      }
    },
  });

  const { fetch: updateLawfulBasis } = useActionRequest({
    api: ({ activityId, bases }: { activityId: string; bases: LawfulBasis[] }) =>
      Api.processingActivities.patchUpdateProcessingActivity(orgId, activityId, {
        name: undefined,
        lawfulBases: bases,
        regions: undefined,
      }),
    onSuccess: () => refreshActivities(),
  });

  const { answers: eeaUkAnswers } = useSurvey(orgId, EEA_UK_SURVEY_NAME);

  const needsEeaUkSurvey = showGdprLawfulBases || !useAquiloDesign;
  if ((!isEnabled && readonly) || (needsEeaUkSurvey && !eeaUkAnswers)) {
    return null;
  }

  const colEnd = Math.ceil(categories.length / 2);
  const cols =
    categories.length >= 4 ? [categories.slice(0, colEnd), categories.slice(colEnd)] : [categories];

  const showCheckbox = variant == "checkbox" && !readonly;
  const showText = variant == "text" && !readonly;

  const checkbox = (
    <Checkbox
      checked={isEnabled}
      onChange={(e, checked) => setChecked(defaultActivity?.id ?? realActivity?.id, checked)}
    />
  );

  const disableRemove = Boolean(realActivity?.autocreationSlug);

  const removeText = (
    <Disableable
      disabledTooltip="You cannot remove this processing activity due your answers to the questions on the Lawful Bases page."
      disabled={disableRemove}
    >
      <MuiLoadingButton
        color="secondary"
        disabled={disableRemove}
        loading={false}
        onClick={(e) => {
          e.preventDefault();
          setChecked(defaultActivity?.id ?? realActivity?.id, !isEnabled);
        }}
      >
        {overrideText ?? (isEnabled ? "Remove from GDPR" : "Add to GDPR")}
      </MuiLoadingButton>
    </Disableable>
  );

  const action = showCheckbox ? checkbox : showText ? removeText : undefined;

  const isGdprProcessingActivity = realActivity?.regions.includes("EEA_UK");

  let extendedOptionsVisibility: LawfulBasisExtendedOptionsVisibility = {
    PUBLIC_INTEREST: eeaUkAnswers[EXTRA_LAWFUL_BASES_PUBLIC_INTEREST_SLUG] === "true",
    VITAL_INTEREST_OF_INDIVIDUAL: eeaUkAnswers[EXTRA_LAWFUL_BASES_VITAL_INTEREST_SLUG] === "true",
  };

  if (useAquiloDesign) {
    return (
      <div className="max-w-[933px]">
        <div
          className={clsx(
            "px-[24px] py-[16px] leading-[20px]",
            "rounded-[6px] border border-solid border-neutral-300",
            "bg-neutral-0",
            "shadow-[0_2px_12px_0_rgba(0,0,0,0.12)]",
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {defaultActivity?.materialIconKey ? (
                React.createElement(MuiIcon[defaultActivity.materialIconKey], {
                  sx: { color: "#9581DA" },
                })
              ) : (
                <Cog6ToothIcon className="h-5 w-5 shrink-0" style={{ color: "#9581DA" }} />
              )}
              <div className="ml-[12px]">
                <div className="flex items-center gap-[8px]">
                  <span className="text-neutral-700 font-medium text-[14px]">
                    {realActivity?.name ?? defaultActivity?.name}
                  </span>
                  {recommendation && (
                    <ToolTip
                      text={
                        recommendation.vendors?.length ? (
                          <div>
                            <div>Because you are using:</div>
                            <div className="mt-[6px] space-y-[4px]">
                              {recommendation.vendors.map((vendor) => (
                                <div key={vendor.name} className="flex items-center gap-[6px]">
                                  {vendor.logoUrl ? (
                                    <img
                                      src={vendor.logoUrl}
                                      alt=""
                                      style={{ width: 16, height: 16, borderRadius: 3 }}
                                    />
                                  ) : (
                                    <span
                                      style={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: 3,
                                        backgroundColor: "#F1EFE8",
                                        display: "inline-block",
                                      }}
                                    />
                                  )}
                                  {vendor.name}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div>Recommended for your business</div>
                        )
                      }
                      inline
                    >
                      {/* A button, not a span: ToolTip opens on hover or the child's
                          :focus-visible, and a span can't take focus — so keyboard users would
                          never see why a purpose was recommended. */}
                      <button
                        type="button"
                        className="flex items-center gap-[4px] text-[13px] font-medium text-primary-600 cursor-default whitespace-nowrap bg-transparent border-none p-0"
                      >
                        <SparklesIcon className="h-[14px] w-[14px]" />
                        Why we recommend
                      </button>
                    </ToolTip>
                  )}
                </div>
                {defaultActivity?.helpText && (
                  <div className="text-neutral-500 font-normal text-[14px]">
                    {defaultActivity.helpText}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-[12px]">
              {showCustomActions && (
                <>
                  <button
                    type="button"
                    className="rounded p-1 hover:bg-neutral-100"
                    aria-label="Edit purpose"
                    onClick={onEdit}
                  >
                    <PencilIcon className="h-5 w-5 text-neutral-500" />
                  </button>
                  <button
                    type="button"
                    className="rounded p-1 hover:bg-neutral-100"
                    aria-label="Remove purpose"
                    onClick={onRemove}
                  >
                    <TrashIcon className="h-5 w-5 text-neutral-500" />
                  </button>
                </>
              )}
              <Toggle
                checked={isEnabled}
                onChange={(checked) => setChecked(defaultActivity?.id ?? realActivity?.id, checked)}
              />
            </div>
          </div>
        </div>
        {isEnabled && realActivity && showGdprLawfulBases && (
          <div
            className={clsx(
              "px-[24px] py-[16px] ml-[24px] mt-[20px] leading-[20px]",
              "rounded-[6px] border border-solid border-neutral-300",
              "bg-white",
              "shadow-[0_2px_12px_0_rgba(0,0,0,0.12)]",
            )}
          >
            <div className="flex items-start">
              <div>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <mask
                    id="mask0_380_42043"
                    style={{ maskType: "alpha" }}
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="24"
                    height="24"
                  >
                    <rect
                      x="24"
                      y="24"
                      width="24"
                      height="24"
                      transform="rotate(-180 24 24)"
                      fill="#D9D9D9"
                    />
                  </mask>
                  <g mask="url(#mask0_380_42043)">
                    <path
                      d="M8.74025 4.50055L8.74025 12.9428C8.74025 13.0326 8.76917 13.1064 8.827 13.164C8.88467 13.2217 8.95833 13.2505 9.048 13.2505L17.6423 13.2505L15.7923 11.4005L16.8463 10.3468L20.5 14.0005L16.8463 17.6543L15.7923 16.6005L17.6423 14.7505L9.048 14.7505C8.54933 14.7505 8.12342 14.574 7.77025 14.2208C7.41708 13.8675 7.2405 13.4415 7.2405 12.9428L7.2405 4.50055H8.74025Z"
                      fill="#1C1B1F"
                    />
                  </g>
                </svg>
              </div>
              <div className="ml-[12px]">
                <div className="flex items-center">
                  <div className="leading-[21px] font-medium text-[14px] text-neutral-900">
                    Lawful Basis for Processing (GDPR)
                  </div>
                  <div className="ml-[12px]">
                    <Toggle
                      checked={isGdprProcessingActivity}
                      onChange={async (checked) => {
                        if (checked) {
                          await addGdprProcessingActivity(realActivity.id);
                        } else {
                          await removeGdprProcessingActivity(realActivity.id);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            {isGdprProcessingActivity && (
              <div className="mt-[12px] pl-[40px]">
                <ProcessingActivityLawfulBasisSelect
                  value={realActivity.lawfulBases}
                  extendedOptionsVisibility={extendedOptionsVisibility}
                  activity={realActivity}
                  onChange={(newBases) =>
                    updateLawfulBasis({ activityId: realActivity.id, bases: newBases })
                  }
                  useAquiloDesign={true}
                />
              </div>
            )}
          </div>
        )}
        {isEnabled && realActivity && (
          <div
            aria-busy={isUpdatingCategories}
            className={clsx(
              "relative",
              "px-[24px] py-[16px] ml-[24px] mt-[20px] leading-[20px]",
              "rounded-[6px] border border-solid border-neutral-300",
              "bg-white",
              "shadow-[0_2px_12px_0_rgba(0,0,0,0.12)]",
            )}
          >
            {isUpdatingCategories && (
              <div className="absolute inset-0 z-10 rounded-[6px] bg-white bg-opacity-50">
                <div className="flex h-full w-full items-center justify-center">
                  <CircularProgress size="2em" aria-label="Saving information categories" />
                </div>
              </div>
            )}
            <div className="mb-[12px] flex items-start gap-[12px]">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="shrink-0"
                aria-hidden="true"
              >
                <path
                  d="M8.74025 4.50055L8.74025 12.9428C8.74025 13.0326 8.76917 13.1064 8.827 13.164C8.88467 13.2217 8.95833 13.2505 9.048 13.2505L17.6423 13.2505L15.7923 11.4005L16.8463 10.3468L20.5 14.0005L16.8463 17.6543L15.7923 16.6005L17.6423 14.7505L9.048 14.7505C8.54933 14.7505 8.12342 14.574 7.77025 14.2208C7.41708 13.8675 7.2405 13.4415 7.2405 12.9428L7.2405 4.50055H8.74025Z"
                  fill="#1C1B1F"
                />
              </svg>
              <div className="leading-[21px] text-[14px] font-medium text-neutral-900">
                Information categories processed
              </div>
              {realActivity.processedPic.length === 0 && (
                <span className="flex-shrink-0">
                  <Pill variant="warning" noMargin>
                    Incomplete
                  </Pill>
                </span>
              )}
            </div>
            {realActivity.processedPic.length === 0 && (
              <p className="mb-[12px] text-[14px] text-neutral-700">
                Select at least one information category to complete this purpose.
              </p>
            )}
            {categories.length > 0 ? (
              <div className="grid grid-cols-1 gap-[12px] md:grid-cols-2">
                {cols.map((col, idx) => (
                  <div key={idx} className="min-w-0 space-y-[12px]">
                    {col.map((pic) => (
                      <SimpleCheckboxControlled
                        key={pic.id}
                        name={`purpose-${realActivity.id}-category-${pic.id}`}
                        label={pic.name}
                        checked={processed.has(pic.id)}
                        disabled={readonly || isUpdatingCategories}
                        preserveCheckedAppearanceWhenDisabled
                        onChange={async (associate) => {
                          try {
                            await assocationMutation.mutateAsync({
                              activityId: realActivity.id,
                              picId: pic.id,
                              associate,
                            });
                          } catch {
                            showSnackbar(
                              "Couldn't save that information category. Please try again.",
                              {
                                variant: "error",
                              },
                            );
                          }
                        }}
                      />
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[14px] text-neutral-700">
                No consumer information categories are available. Record the information you collect
                in the{" "}
                <Link
                  to={Routes.dataMap.dataFlows.dashboard}
                  className="text-primary-600 underline"
                >
                  Data Flows dashboard
                </Link>{" "}
                first.
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <Callout
      variant={CalloutVariant.LightPurpleBordered}
      icon={defaultActivity?.materialIconKey}
      title={realActivity?.name ?? defaultActivity?.name}
      subtitle={readonly ? enabledLabel : defaultActivity?.helpText}
      collapsed={open ? false : !collapsible || !isEnabled || readonly}
      collapsible={collapsible}
      hideCollapseToggle
      action={action}
    >
      {categories.length > 0 ? (
        <div className="flex flex-row flex-between px-xl">
          {cols.map((col, idx) => {
            return (
              <div className="flex-col w-50" key={idx}>
                {col.map((pic) => {
                  const activityId = realActivity?.id;
                  const isUpdating = localProcessedUpdate.has(pic.id);
                  const isChecked = processed.has(pic.id);

                  if (!activityId) {
                    return null;
                  }

                  return (
                    <CheckboxControlLabel
                      key={pic.id}
                      label={pic.name}
                      loading={isUpdating}
                      checked={isChecked}
                      onChange={(e, checked) => {
                        assocationMutation.mutate({
                          activityId: activityId,
                          picId: pic.id,
                          associate: checked,
                        });
                      }}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyInfo>No personal information is collected</EmptyInfo>
      )}
    </Callout>
  );
};
